import React, { useEffect, useState } from 'react';
import { ShieldAlert, LogOut } from 'lucide-react';
import api from '../utils/api';

/**
 * ImpersonationBanner — sticky red banner shown whenever an admin is logged in
 * as another user via the /admin/impersonate/start action.
 *
 * Provides:
 *  - Persistent safety signal that this is NOT the user's real session
 *  - Original admin email + reason (from the impersonation audit trail)
 *  - Impersonated user email
 *  - One-click Exit that closes the audit row on the server, restores
 *    the original admin token, and redirects back to /admin/users.
 */
const ImpersonationBanner = () => {
  // Reactive: re-read localStorage whenever another tab (or our own
  // Exit handler) mutates the impersonation keys. Prior implementation
  // captured `impersonating` once at mount, so the banner kept
  // rendering after Exit if the parent shell didn't remount.
  const readState = () => ({
    impersonating: localStorage.getItem('impersonating') === 'true',
    originalToken: localStorage.getItem('original_token'),
    targetEmail: localStorage.getItem('impersonated_user_email') || 'this user',
    adminEmail: localStorage.getItem('impersonator_email') || '',
    reason: localStorage.getItem('impersonation_reason') || '',
  });
  const [state, setState] = useState(readState);
  const { impersonating, originalToken, targetEmail, adminEmail, reason } = state;
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    const refresh = () => setState(readState());
    window.addEventListener('storage', refresh);
    // Also poll once/second for same-tab mutations (localStorage 'storage'
    // event does not fire in the tab that made the change).
    const t = setInterval(refresh, 1000);
    return () => {
      window.removeEventListener('storage', refresh);
      clearInterval(t);
    };
  }, []);

  // Toggle body attribute so page layouts can offset their top padding
  // whenever the banner is active. Cleans up on unmount / exit.
  useEffect(() => {
    if (impersonating && originalToken) {
      document.body.setAttribute('data-impersonating', 'true');
      return () => {
        document.body.removeAttribute('data-impersonating');
      };
    }
    document.body.removeAttribute('data-impersonating');
    return undefined;
  }, [impersonating, originalToken]);

  if (!impersonating || !originalToken) return null;

  const stop = async () => {
    if (exiting) return;
    setExiting(true);
    try {
      // Ask the backend to close the audit row + mint a fresh admin JWT.
      // Best-effort: if the call fails we still restore the original
      // token locally so the admin can regain control.
      let newAdminToken = null;
      try {
        const res = await api.post('/admin/impersonate/stop', {});
        newAdminToken = res.data?.token || null;
      } catch (_) {
        newAdminToken = null;
      }

      // Restore the admin token + user object. Prefer the freshly-minted
      // token if the stop endpoint succeeded; fall back to the stashed
      // original_token if not.
      localStorage.setItem('token', newAdminToken || originalToken);
      const originalUser = localStorage.getItem('original_user');
      if (originalUser) {
        localStorage.setItem('user', originalUser);
      } else {
        localStorage.removeItem('user');
      }

      // Full sweep of impersonation crumbs — nothing about the target
      // account may survive Exit.
      [
        'original_token', 'original_user',
        'impersonating', 'impersonated_user_email',
        'impersonator_email', 'impersonation_reason',
        'impersonation_started_at', 'impersonation_session_id',
      ].forEach((k) => localStorage.removeItem(k));

      // Also wipe sessionStorage + any React Query / axios-cached
      // impersonation state so subsequent /me, bookings, etc. requests
      // use the fresh admin identity.
      try { sessionStorage.clear(); } catch (_) { /* noop */ }
      try {
        Object.keys(localStorage)
          .filter((k) => k.startsWith('rq-') || k.startsWith('swr-') || k.startsWith('ievntur-cache-'))
          .forEach((k) => localStorage.removeItem(k));
      } catch (_) { /* noop */ }

      // 2026-08-28 P0 — hard nav via replace() so back button never
      // returns to the impersonated surface. Also clears in-memory
      // React state + any long-lived fetches. window.location.href
      // was used previously but some PWAs treat it as a soft nav.
      window.location.replace('/admin/users');
    } finally {
      setExiting(false);
    }
  };

  return (
    <div
      className="fixed top-0 inset-x-0 z-[9998] bg-red-600 text-white shadow-md"
      data-testid="impersonation-banner"
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
    >
      <div className="max-w-7xl mx-auto px-2 sm:px-4 py-[3px] sm:py-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 sm:gap-2 min-w-0 flex-1">
          <ShieldAlert className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
          <span className="truncate text-[10px] sm:text-sm leading-tight">
            <span className="hidden sm:inline">Impersonating </span>
            <strong className="font-semibold" data-testid="banner-target-email">{targetEmail}</strong>
            {adminEmail && (
              <span className="hidden sm:inline">
                {' '}as <strong data-testid="banner-admin-email">{adminEmail}</strong>
              </span>
            )}
            {reason && (
              <span className="hidden md:inline" title={reason}>
                {' — reason: '}
                <em className="not-italic">{reason.length > 60 ? `${reason.slice(0, 60)}…` : reason}</em>
              </span>
            )}
            <span className="hidden sm:inline"> — all actions logged.</span>
          </span>
        </div>
        <button
          type="button"
          onClick={stop}
          disabled={exiting}
          className="flex items-center gap-1 bg-white text-red-700 font-medium rounded px-2 sm:px-3 py-[1px] sm:py-1 text-[10px] sm:text-xs hover:bg-red-50 transition shrink-0 disabled:opacity-70"
          data-testid="stop-impersonation-btn"
          aria-label="Exit impersonation and return to admin"
        >
          <LogOut className="w-3 h-3" />
          {exiting ? 'Exiting…' : 'Exit'}
        </button>
      </div>
    </div>
  );
};

export default ImpersonationBanner;
